import { Play } from '@tunasong/icons'
import { addToolBreadcrumb } from '@tunasong/models'
import { getModeLabOptions, type ModeLabMode } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { Button, useSearchParams } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { useTheoryLevel, type TheoryLevel } from '../theory-copy/use-theory-copy.js'
import { ModeCharacteristicChord } from './mode-characteristic-chord.js'
import { parseModeParam, parseTonicParam } from './mode-lab-params.js'
import { ModePicker } from './mode-picker.js'
import { ModeScaleDisplay } from './mode-scale-display.js'
import { useModeLabPlayback } from './mode-lab-playback.js'
import styles from './mode-lab.module.css'

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * §8.6 per-level register table for the parallel-reference name. The tonic
 * is supplied by the caller so the resolver doesn't need to know about
 * spelling.
 *
 *  - Beginner: "minor" / "major" (drop "natural", drop mode-name pedantry)
 *  - Intermediate: "natural minor" / "major"
 *  - Advanced: "Aeolian" / "Ionian"
 *
 * `'off'` falls back to the intermediate text — Mode Lab is itself a
 * teaching surface so we still need *some* label even when chips are off.
 */
const parallelReferenceName = (
  parallelType: 'major' | 'minor',
  level: TheoryLevel
): string => {
  if (level === 'beginner') {
    return parallelType === 'minor' ? 'minor' : 'major'
  }
  if (level === 'advanced') {
    return parallelType === 'minor' ? 'Aeolian' : 'Ionian'
  }
  // 'intermediate' and 'off' both use the intermediate register
  return parallelType === 'minor' ? 'natural minor' : 'major'
}

/**
 * Mode Lab v1 host. Owns mode + tonic + comparing state in local React
 * state; no persistence (§2 Q6). Spec: docs/superpowers/specs/2026-05-18-mode-lab-design.md
 *
 * Bowie §8 verdicts wired in:
 *  - §8.1/§8.2 — parallel vamps encoded in `mode-lab-options.ts`
 *  - §8.3 — characteristic chord is the major triad named in the locked table
 *  - §8.4 — enharmonic spelling deferred to `ScaleLib.getNotes`
 *  - §8.5 — switching mode resets tonic to canonical default (no per-mode memory)
 *  - §8.6 — Compare-toggle label resolves per theoryLevel
 */
export const ModeLab: FC = () => {
  const [searchParams] = useSearchParams()
  const [mode, setMode] = useState<ModeLabMode>(() => parseModeParam(searchParams.get('mode')))
  const [tonic, setTonic] = useState<NoteName>(() =>
    parseTonicParam(searchParams.get('tonic'), parseModeParam(searchParams.get('mode')))
  )
  const [comparing, setComparing] = useState(false)
  const level = useTheoryLevel()

  // Polish 6/6 — tool telemetry. Sentry breadcrumb on mount; attaches to any
  // subsequent error report so we can measure tool usage via Sentry. Empty
  // dep array — fires once per tool open.
  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'mode-lab' })
  }, [])

  const options = getModeLabOptions(mode, tonic)

  const scale = comparing ? options.parallelScale : options.modeScale
  const captionLexiconId = comparing ? 'mode-characteristic-note' : options.modeLexiconId
  const vamp = comparing ? options.parallelVamp : options.modeVamp

  const { play, stop, isPlaying } = useModeLabPlayback({ vamp, loop: true })

  const parallelType: 'major' | 'minor' =
    options.parallelScale.type === 'major' ? 'major' : 'minor'
  const compareLabel = comparing
    ? `Back to ${tonic} ${capitalize(mode)}`
    : `Compare to ${tonic} ${parallelReferenceName(parallelType, level)}`

  const handlePickerChange = (nextMode: ModeLabMode, nextTonic: NoteName) => {
    stop()
    setMode(nextMode)
    setTonic(nextTonic)
    setComparing(false)
  }
  const handleToggleCompare = () => {
    stop()
    setComparing(c => !c)
  }
  const handlePlayClick = () => {
    if (isPlaying) {
      stop()
    } else {
      play()
    }
  }

  const relatedTools: RelatedTool[] = [
    { targetRoute: `/quiz?key=${tonic}`, lexiconId: 'crosslink-mode-to-quiz' },
    { targetRoute: `/reharm?key=${tonic}`, lexiconId: 'crosslink-mode-to-reharm' },
  ]

  return (
    <div className={styles.lab}>
      <ModePicker mode={mode} tonic={tonic} onChange={handlePickerChange} />
      <ModeScaleDisplay
        scale={scale}
        characteristicDegreeIndex={options.characteristicDegreeIndex}
        captionLexiconId={captionLexiconId}
      />
      {!comparing ? (
        <ModeCharacteristicChord
          chord={options.characteristicChord}
          modeLexiconId={options.modeLexiconId}
        />
      ) : null}
      <div className={styles.controls}>
        <Button
          variant="contained"
          color="primary"
          startIcon={isPlaying ? null : <Play />}
          onClick={handlePlayClick}
        >
          {isPlaying ? 'Stop' : 'Play vamp'}
        </Button>
        <Button variant="outlined" onClick={handleToggleCompare}>
          {compareLabel}
        </Button>
      </div>
      <RelatedTools tools={relatedTools} />
    </div>
  )
}
